/**
 * Headings and next steps for the refusal messages shown on the `/error` page,
 * matched on the message verbatim.
 */

export interface AuthErrorCopy {
  heading: string;
  /** What to do about it. Null for an unrecognised description. */
  hint: string | null;
  /** Whether "Try again" is worth offering. */
  canRetry: boolean;
}

const DENIALS: Record<string, AuthErrorCopy> = {
  'No invitation was found for this account.': {
    heading: 'No invitation for this account',
    hint: 'Nebari is invite-only. Ask an administrator to invite the email address you just signed in with, then sign in again.',
    canRetry: false,
  },
  'Add Google from an invite, or ask an administrator.': {
    heading: 'Use your password to sign in',
    hint: 'Google cannot confirm it owns this address, so it cannot be added to your account here. Sign in with your password instead. If you need Google sign-in on this account, ask an administrator.',
    canRetry: false,
  },
  'Your identity provider did not verify this email address.': {
    heading: 'Email address not verified',
    hint: 'Google did not confirm this address belongs to you. Verify the email with Google, then sign in again.',
    canRetry: false,
  },
  'Your account is not set up in Nebari.': {
    heading: 'No Nebari account yet',
    hint: 'You signed in, but no Nebari account is linked to this address. Ask an administrator to invite you, then sign in again.',
    canRetry: false,
  },
  'Your Nebari account has been deactivated.': {
    heading: 'Account deactivated',
    hint: 'An administrator has turned off your access to Nebari. If you think that is a mistake, ask them to restore it.',
    canRetry: false,
  },
  'Sign-in is temporarily unavailable. Please try again.': {
    heading: 'Sign-in temporarily unavailable',
    hint: 'Something on our side did not respond. Your invitation is still valid — try again in a moment.',
    canRetry: true,
  },
};

export function authErrorCopy(error: string, description: string): AuthErrorCopy {
  const known = DENIALS[description.trim()];
  if (known) return known;
  return { heading: 'Access Denied', hint: null, canRetry: error !== 'access_denied' };
}
